import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import { Plus } from "lucide-react";
import PropTypes from "prop-types";

// Single shared font size used for all body text in this component
const BODY_FONT_SIZE = "0.8rem";

// Shared sx applied to every body Typography so font is consistent
const bodyText = {
  fontSize: BODY_FONT_SIZE,
  lineHeight: 1.6,
};

/**
 * Pure presentational component.
 * Receives `items` (array of recommendation objects for ONE subsystem)
 * and renders them. All fetching and grouping is handled by MicrobiomePage.
 */
const GutRecommendations = ({ items = [], recipe = null }) => {
  const theme = useTheme();

  if (!items.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ ...bodyText, color: "text.secondary" }}>
          No recommendations available for this category.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={1}>
      {items.map((item, index) => (
        <Grid key={index} size={12} sx={{ px: 2 }}>

          {item.title && (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {item.title}
            </Typography>
          )}

          {item.description && (
            <Typography sx={{ ...bodyText, mb: 0.75 }}>
              {item.description}
            </Typography>
          )}

          {item.pointer_intro && (
            <Accordion
              elevation={0}
              disableGutters
              sx={{
                mt: 1,
                bgcolor: theme.palette.mode === "light" ? "grey.100" : "greydark.C700",
                borderRadius: "8px !important",
                border: `1px solid ${theme.palette.divider}`,
                "&:before": { display: "none" },
                "&.Mui-expanded": { borderRadius: "8px !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<Plus size={18} />}
                sx={{
                  minHeight: 40,
                  "& .MuiAccordionSummary-content": { my: 0.75 },
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(45deg)",
                  },
                }}
              >
                <Typography sx={{ ...bodyText, fontStyle: "italic" }}>
                  {item.pointer_intro}
                </Typography>
              </AccordionSummary>

              {item.pointers && (
                <AccordionDetails sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
                  <List dense disablePadding sx={{ listStyleType: "disc", pl: 2.5 }}>
                    {item.pointers.split(",").map((pointer, i) => (
                      <ListItem key={i} disableGutters sx={{ display: "list-item", pl: 0 }}>
                        <ListItemText
                          primary={pointer.trim()}
                          sx={{ "& .MuiTypography-root": { fontSize: BODY_FONT_SIZE } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              )}
            </Accordion>
          )}

          {index < items.length - 1 && <Divider sx={{ mt: 2, mb: 1 }} />}
        </Grid>
      ))}

      {/* ── Single recipe block rendered once after all items ── */}
      {recipe?.recipe_title && (
        <Grid size={12} sx={{ px: 2, mt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Accordion
            elevation={0}
            disableGutters
            sx={{
              bgcolor: theme.palette.mode === "light" ? "grey.100" : "greydark.C700",
              borderRadius: "8px !important",
              border: `1px solid ${theme.palette.divider}`,
              "&:before": { display: "none" },
              "&.Mui-expanded": { borderRadius: "8px !important" },
            }}
          >
            <AccordionSummary
              expandIcon={<Plus size={18} />}
              sx={{
                minHeight: 40,
                "& .MuiAccordionSummary-content": { my: 0.75 },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(45deg)",
                },
              }}
            >
              <Typography sx={{ ...bodyText, fontWeight: 600 }}>
                Recipe: {recipe.recipe_title}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
              {recipe.ingredients && (
                <Typography sx={{ ...bodyText, mb: 0.75 }}>
                  <strong>Ingredients:</strong> {recipe.ingredients}
                </Typography>
              )}
              {recipe.preparation && (
                <Typography sx={{ ...bodyText, mb: 0.75 }}>
                  <strong>Preparation:</strong> {recipe.preparation}
                </Typography>
              )}
              {recipe.conclusion && (
                <Typography sx={{ ...bodyText, fontStyle: "italic" }}>
                  {recipe.conclusion}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </Grid>
  );
};

export default GutRecommendations;